import { services } from './services';

// ============= TYPES =============

export interface CityConfig {
  slug: string;
  name: string;
  county: string;
  population?: number;
  description: string;
  landmarks?: string[];
  coordinates: { lat: number; lng: number };
  nearbyCity?: string[];
}

export interface CountyConfig {
  slug: string;
  name: string;
  state: string;
  description: string;
  population?: number;
  seatCity: string;
  coordinates: { lat: number; lng: number };
  cities: CityConfig[];
}

export interface ServiceCityConfig {
  serviceId: string;
  citySlug: string;
  countySlug: string;
  headline: string;
  subheadline: string;
  localFacts: string[];
  testimonial?: {
    quote: string;
    author: string;
    business: string;
  };
}

// ============= HURON COUNTY =============

const huronCountyCities: CityConfig[] = [
  {
    slug: 'bad-axe',
    name: 'Bad Axe',
    county: 'huron',
    population: 2936,
    description: 'The county seat of Huron County, Bad Axe is a thriving community at the heart of Michigan\'s Thumb region.',
    landmarks: ['Huron County Fairgrounds', 'Bad Axe City Park'],
    coordinates: { lat: 43.8017, lng: -83.0008 },
    nearbyCity: ['harbor-beach', 'ubly', 'pigeon']
  },
  {
    slug: 'harbor-beach',
    name: 'Harbor Beach',
    county: 'huron',
    population: 1637,
    description: 'Known as the "Huron City," Harbor Beach features the largest man-made freshwater harbor in the world.',
    landmarks: ['Harbor Beach Lighthouse', 'Frank Murphy Memorial Museum'],
    coordinates: { lat: 43.8456, lng: -82.6514 },
    nearbyCity: ['bad-axe', 'port-hope', 'ubly']
  },
  {
    slug: 'caseville',
    name: 'Caseville',
    county: 'huron',
    population: 777,
    description: 'A popular resort town on Saginaw Bay, Caseville is famous for the annual Cheeseburger in Caseville Festival.',
    landmarks: ['Caseville County Park', 'Sleeper State Park'],
    coordinates: { lat: 43.9431, lng: -83.2722 },
    nearbyCity: ['pigeon', 'port-austin', 'elkton']
  },
  {
    slug: 'elkton',
    name: 'Elkton',
    county: 'huron',
    population: 797,
    description: 'A small village in Huron County with strong agricultural roots and a close-knit community.',
    landmarks: ['Elkton Park'],
    coordinates: { lat: 43.8267, lng: -83.1797 },
    nearbyCity: ['pigeon', 'bad-axe', 'caseville']
  },
  {
    slug: 'kinde',
    name: 'Kinde',
    county: 'huron',
    population: 441,
    description: 'Home to the famous "Kinde Polka Fest," this village celebrates its Polish heritage with annual festivities.',
    landmarks: ['Kinde Polka Fest Grounds'],
    coordinates: { lat: 43.9356, lng: -82.9942 },
    nearbyCity: ['port-austin', 'bad-axe', 'caseville']
  },
  {
    slug: 'owendale',
    name: 'Owendale',
    county: 'huron',
    population: 247,
    description: 'A quiet village in Huron County known for its friendly residents and rural charm.',
    landmarks: [],
    coordinates: { lat: 43.7264, lng: -83.2433 },
    nearbyCity: ['pigeon', 'sebewaing', 'elkton']
  },
  {
    slug: 'pigeon',
    name: 'Pigeon',
    county: 'huron',
    population: 1203,
    description: 'A vibrant community in Huron County with local shops, restaurants, and access to Saginaw Bay recreation.',
    landmarks: ['Pigeon Farmers Market', 'Pigeon River'],
    coordinates: { lat: 43.8306, lng: -83.2697 },
    nearbyCity: ['caseville', 'elkton', 'owendale', 'bad-axe']
  },
  {
    slug: 'port-austin',
    name: 'Port Austin',
    county: 'huron',
    population: 664,
    description: 'Located at the tip of the Thumb, Port Austin is a premier destination for tourism, recreation, and beautiful Lake Huron views.',
    landmarks: ['Turnip Rock', 'Port Austin Reef Lighthouse', 'Bird Creek Park'],
    coordinates: { lat: 44.0492, lng: -82.9986 },
    nearbyCity: ['caseville', 'kinde', 'bad-axe']
  },
  {
    slug: 'port-hope',
    name: 'Port Hope',
    county: 'huron',
    population: 254,
    description: 'A scenic village on the Lake Huron shoreline, Port Hope offers quiet beaches and historic charm.',
    landmarks: ['Port Hope Chimney', 'Stafford County Park'],
    coordinates: { lat: 43.9394, lng: -82.7133 },
    nearbyCity: ['harbor-beach', 'bad-axe']
  },
  {
    slug: 'sebewaing',
    name: 'Sebewaing',
    county: 'huron',
    population: 1759,
    description: 'Known as "Michigan\'s Sugar Town," Sebewaing is home to the Michigan Sugar Festival and a major sugar beet processing plant.',
    landmarks: ['Michigan Sugar Company', 'Sebewaing Brewing Company'],
    coordinates: { lat: 43.7323, lng: -83.4508 },
    nearbyCity: ['owendale', 'pigeon', 'unionville']
  },
  {
    slug: 'ubly',
    name: 'Ubly',
    county: 'huron',
    population: 822,
    description: 'A small, welcoming village in Huron County with excellent schools and strong community values.',
    landmarks: ['Ubly Heights Golf Course'],
    coordinates: { lat: 43.7114, lng: -82.9300 },
    nearbyCity: ['bad-axe', 'harbor-beach', 'cass-city']
  }
];

// ============= TUSCOLA COUNTY =============

const tuscolaCountyCities: CityConfig[] = [
  {
    slug: 'caro',
    name: 'Caro',
    county: 'tuscola',
    population: 4091,
    description: 'The county seat of Tuscola County, Caro hosts the annual Tuscola County Fair and offers a charming downtown district.',
    landmarks: ['Tuscola County Fairgrounds', 'Caro Regional Center'],
    coordinates: { lat: 43.4903, lng: -83.3964 },
    nearbyCity: ['vassar', 'cass-city', 'fairgrove']
  },
  {
    slug: 'vassar',
    name: 'Vassar',
    county: 'tuscola',
    population: 2697,
    description: 'A historic city on the Cass River, Vassar features beautiful Victorian architecture and the annual Vassar Riverfest.',
    landmarks: ['Vassar Riverfest', 'Historic Downtown'],
    coordinates: { lat: 43.3717, lng: -83.5831 },
    nearbyCity: ['caro', 'millington', 'mayville']
  },
  {
    slug: 'cass-city',
    name: 'Cass City',
    county: 'tuscola',
    population: 2428,
    description: 'A thriving community in Tuscola County with excellent healthcare, schools, and a strong local business district.',
    landmarks: ['Hills and Dales General Hospital', 'Cass City Recreation Park'],
    coordinates: { lat: 43.6003, lng: -83.1747 },
    nearbyCity: ['caro', 'ubly', 'kingston']
  },
  {
    slug: 'fairgrove',
    name: 'Fairgrove',
    county: 'tuscola',
    population: 548,
    description: 'A small village known for its agricultural heritage and the annual Fairgrove Goose Festival.',
    landmarks: ['Fairgrove Goose Festival'],
    coordinates: { lat: 43.5214, lng: -83.5408 },
    nearbyCity: ['caro', 'akron', 'reese']
  },
  {
    slug: 'gagetown',
    name: 'Gagetown',
    county: 'tuscola',
    population: 336,
    description: 'A quiet village in Tuscola County with deep agricultural roots and a welcoming community.',
    landmarks: [],
    coordinates: { lat: 43.6564, lng: -83.2472 },
    nearbyCity: ['cass-city', 'owendale', 'unionville']
  },
  {
    slug: 'kingston',
    name: 'Kingston',
    county: 'tuscola',
    population: 449,
    description: 'A small, family-friendly village in Tuscola County with excellent schools and a strong sense of community.',
    landmarks: ['Kingston Days Festival'],
    coordinates: { lat: 43.4078, lng: -83.1775 },
    nearbyCity: ['cass-city', 'caro', 'marlette']
  },
  {
    slug: 'mayville',
    name: 'Mayville',
    county: 'tuscola',
    population: 990,
    description: 'A historic village at the southern edge of Tuscola County with access to beautiful fishing lakes.',
    landmarks: ['Fish Point Wildlife Area'],
    coordinates: { lat: 43.3406, lng: -83.3567 },
    nearbyCity: ['vassar', 'kingston', 'caro']
  },
  {
    slug: 'millington',
    name: 'Millington',
    county: 'tuscola',
    population: 1071,
    description: 'Gateway to the Thumb, Millington offers convenient access to I-75 and serves as a hub for local commerce.',
    landmarks: ['Millington Village Park'],
    coordinates: { lat: 43.2803, lng: -83.5297 },
    nearbyCity: ['vassar', 'clio', 'frankenmuth']
  },
  {
    slug: 'akron',
    name: 'Akron',
    county: 'tuscola',
    population: 395,
    description: 'A small agricultural village in Tuscola County with strong community ties and local events.',
    landmarks: [],
    coordinates: { lat: 43.5656, lng: -83.5361 },
    nearbyCity: ['fairgrove', 'caro', 'unionville']
  }
];

// ============= SANILAC COUNTY =============

const sanilacCountyCities: CityConfig[] = [
  {
    slug: 'sandusky',
    name: 'Sandusky',
    county: 'sanilac',
    population: 2607,
    description: 'The county seat of Sanilac County, Sandusky is a vibrant community with a historic downtown and the Sanilac County Fair.',
    landmarks: ['Sanilac County Fairgrounds', 'Sanilac County Historic Village'],
    coordinates: { lat: 43.4206, lng: -82.8292 },
    nearbyCity: ['croswell', 'marlette', 'deckerville']
  },
  {
    slug: 'croswell',
    name: 'Croswell',
    county: 'sanilac',
    population: 2447,
    description: 'Known as the "Pioneer Sugar City," Croswell features the historic Croswell Swinging Bridge and strong community events.',
    landmarks: ['Croswell Swinging Bridge', 'Croswell Berry Farm'],
    coordinates: { lat: 43.2753, lng: -82.6206 },
    nearbyCity: ['lexington', 'sandusky', 'peck']
  },
  {
    slug: 'lexington',
    name: 'Lexington',
    county: 'sanilac',
    population: 1178,
    description: 'A charming lakeside village on Lake Huron, Lexington offers beautiful beaches, a historic downtown, and the annual Thumbfest music festival.',
    landmarks: ['Lexington State Harbor', 'Thumbfest Music Festival'],
    coordinates: { lat: 43.2678, lng: -82.5286 },
    nearbyCity: ['croswell', 'port-sanilac', 'forestville']
  },
  {
    slug: 'marlette',
    name: 'Marlette',
    county: 'sanilac',
    population: 1879,
    description: 'A thriving community in Sanilac County with excellent schools, healthcare, and a growing business district.',
    landmarks: ['Marlette Community Hospital'],
    coordinates: { lat: 43.3275, lng: -83.0808 },
    nearbyCity: ['sandusky', 'brown-city', 'kingston']
  },
  {
    slug: 'port-sanilac',
    name: 'Port Sanilac',
    county: 'sanilac',
    population: 602,
    description: 'A picturesque harbor village on Lake Huron, Port Sanilac is known for its historic lighthouse and waterfront charm.',
    landmarks: ['Port Sanilac Lighthouse', 'Sanilac County Historic Village'],
    coordinates: { lat: 43.4297, lng: -82.5417 },
    nearbyCity: ['lexington', 'forestville', 'deckerville']
  },
  {
    slug: 'applegate',
    name: 'Applegate',
    county: 'sanilac',
    population: 244,
    description: 'A small, friendly village in Sanilac County with a peaceful rural atmosphere.',
    landmarks: [],
    coordinates: { lat: 43.3592, lng: -82.6364 },
    nearbyCity: ['croswell', 'sandusky', 'carsonville']
  },
  {
    slug: 'carsonville',
    name: 'Carsonville',
    county: 'sanilac',
    population: 476,
    description: 'A small village in Sanilac County with a tight-knit community and agricultural heritage.',
    landmarks: [],
    coordinates: { lat: 43.4481, lng: -82.6597 },
    nearbyCity: ['port-sanilac', 'sandusky', 'deckerville']
  },
  {
    slug: 'deckerville',
    name: 'Deckerville',
    county: 'sanilac',
    population: 807,
    description: 'Home to the Deckerville Eagles, this village has strong school spirit and community pride.',
    landmarks: ['Deckerville Community Schools'],
    coordinates: { lat: 43.5275, lng: -82.7361 },
    nearbyCity: ['sandusky', 'carsonville', 'minden-city']
  },
  {
    slug: 'forestville',
    name: 'Forestville',
    county: 'sanilac',
    population: 108,
    description: 'A tiny lakeside community in Sanilac County with access to Lake Huron recreation.',
    landmarks: [],
    coordinates: { lat: 43.6539, lng: -82.5956 },
    nearbyCity: ['port-sanilac', 'lexington', 'harbor-beach']
  },
  {
    slug: 'melvin',
    name: 'Melvin',
    county: 'sanilac',
    population: 197,
    description: 'A small, rural village in Sanilac County surrounded by farmland.',
    landmarks: [],
    coordinates: { lat: 43.1883, lng: -82.8500 },
    nearbyCity: ['peck', 'brown-city', 'croswell']
  },
  {
    slug: 'minden-city',
    name: 'Minden City',
    county: 'sanilac',
    population: 263,
    description: 'A small village in Sanilac County with agricultural roots and a peaceful atmosphere.',
    landmarks: [],
    coordinates: { lat: 43.6700, lng: -82.7686 },
    nearbyCity: ['deckerville', 'ubly', 'forestville']
  },
  {
    slug: 'peck',
    name: 'Peck',
    county: 'sanilac',
    population: 600,
    description: 'A small village in Sanilac County known for the Peck Pirates and community events.',
    landmarks: [],
    coordinates: { lat: 43.2586, lng: -82.8206 },
    nearbyCity: ['croswell', 'sandusky', 'melvin']
  },
  {
    slug: 'brown-city',
    name: 'Brown City',
    county: 'sanilac',
    population: 1386,
    description: 'Located in both Sanilac and Lapeer counties, Brown City is known for the annual Brown City PRCA Rodeo.',
    landmarks: ['Brown City PRCA Rodeo'],
    coordinates: { lat: 43.2114, lng: -82.9892 },
    nearbyCity: ['marlette', 'peck', 'melvin']
  }
];

// ============= COUNTY CONFIGURATIONS =============

export const counties: CountyConfig[] = [
  {
    slug: 'huron-county',
    name: 'Huron County',
    state: 'Michigan',
    description: 'Located at the tip of Michigan\'s Thumb, Huron County offers beautiful Lake Huron shorelines, charming small towns, and a thriving agricultural community. From the resort town of Caseville to the historic port of Harbor Beach, Huron County businesses are ready for digital growth.',
    population: 30000,
    seatCity: 'bad-axe',
    coordinates: { lat: 43.8500, lng: -83.0000 },
    cities: huronCountyCities
  },
  {
    slug: 'tuscola-county',
    name: 'Tuscola County',
    state: 'Michigan',
    description: 'The heart of Michigan\'s Thumb region, Tuscola County combines agricultural heritage with modern community amenities. From the county seat of Caro to the healthcare hub of Cass City, Tuscola County businesses are ready to connect with more customers online.',
    population: 52000,
    seatCity: 'caro',
    coordinates: { lat: 43.4800, lng: -83.4000 },
    cities: tuscolaCountyCities
  },
  {
    slug: 'sanilac-county',
    name: 'Sanilac County',
    state: 'Michigan',
    description: 'Stretching along the Lake Huron shoreline, Sanilac County features stunning beaches, historic lighthouses, and vibrant communities. From the charming harbor of Lexington to the county seat of Sandusky, Sanilac County businesses deserve expert digital marketing.',
    population: 40000,
    seatCity: 'sandusky',
    coordinates: { lat: 43.4000, lng: -82.8000 },
    cities: sanilacCountyCities
  }
];

// ============= HELPER FUNCTIONS =============

export function getCountyBySlug(slug: string): CountyConfig | undefined {
  return counties.find(c => c.slug === slug);
}

export function getCityBySlug(citySlug: string, countySlug?: string): CityConfig | undefined {
  if (countySlug) {
    const county = getCountyBySlug(countySlug);
    return county?.cities.find(c => c.slug === citySlug);
  }
  // Search all counties
  for (const county of counties) {
    const city = county.cities.find(c => c.slug === citySlug);
    if (city) return city;
  }
  return undefined;
}

export function getCityCounty(citySlug: string): CountyConfig | undefined {
  for (const county of counties) {
    if (county.cities.some(c => c.slug === citySlug)) {
      return county;
    }
  }
  return undefined;
}

export function getAllCities(): CityConfig[] {
  return counties.flatMap(c => c.cities);
}

export function getNearbyCities(citySlug: string): CityConfig[] {
  const city = getCityBySlug(citySlug);
  if (!city || !city.nearbyCity) return [];
  
  return city.nearbyCity
    .map(slug => getCityBySlug(slug))
    .filter((c): c is CityConfig => c !== undefined);
}

export function getServiceById(serviceId: string) {
  return services.find(s => s.id === serviceId);
}

export function getAllServiceIds(): string[] {
  return services.map(s => s.id);
}

// Generate service-city specific content
export function generateServiceCityContent(serviceId: string, city: CityConfig, county: CountyConfig) {
  const service = getServiceById(serviceId);
  if (!service) return null;

  return {
    serviceId,
    citySlug: city.slug,
    countySlug: county.slug,
    headline: `${service.title} in ${city.name}, Michigan`,
    subheadline: `Professional ${service.title.toLowerCase()} services for ${city.name} businesses in ${county.name}`,
    metaTitle: `${service.title} ${city.name} MI | ${county.name} Digital Marketing`,
    metaDescription: `Expert ${service.title.toLowerCase()} services for businesses in ${city.name}, Michigan. Valentine Digital helps ${county.name} businesses grow with proven digital marketing strategies.`,
    localFacts: [
      `Serving businesses throughout ${city.name} and ${county.name}`,
      city.population ? `${city.name} population: ${city.population.toLocaleString()} residents` : '',
      city.landmarks?.length ? `Local landmarks: ${city.landmarks.slice(0, 2).join(', ')}` : '',
      `Part of Michigan's Thumb region`
    ].filter(Boolean)
  };
}

// Get all service-city combinations for sitemap generation
export function getAllServiceCityRoutes(): Array<{ county: string; city: string; service: string }> {
  const routes: Array<{ county: string; city: string; service: string }> = [];
  
  for (const county of counties) {
    for (const city of county.cities) {
      for (const service of services) {
        routes.push({
          county: county.slug,
          city: city.slug,
          service: service.id
        });
      }
    }
  }
  
  return routes;
}

// Get service display name for URLs (formatted nicely)
export function formatServiceForDisplay(serviceId: string): string {
  const service = getServiceById(serviceId);
  return service?.title || serviceId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Format city name for display
export function formatCityForDisplay(citySlug: string): string {
  const city = getCityBySlug(citySlug);
  return city?.name || citySlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
